import type { ReactNode } from 'react'
import { useState } from 'react'
import { Button, ConfirmationDialog, toastManager } from '@systemeio/components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next/pages'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { cancelSubscription } from 'modules/profile/manage-subscriptions/api/cancel-subscriptions-api'
import { subscriptionKeys } from 'modules/profile/manage-subscriptions/hooks/use-manage-subscriptions'
import type {
  ManageSubscriptionsApiInterface,
  ManageSubscriptionsInterface,
} from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface'

interface CancelSubscriptionModalProps {
  opened: boolean
  onClose: () => void
  subscription: ManageSubscriptionsInterface
}

function CancelSubscriptionModal({
  opened,
  onClose,
  subscription,
}: CancelSubscriptionModalProps): ReactNode {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const queryClient = useQueryClient()
  const { mutateAsync: cancel, isPending: isFetching } = useMutation({
    mutationFn: async () =>
      cancelSubscription(
        subscription.id,
        subscription.subscriptionItems[0].id,
        subscription.paymentProcessor,
      ),
    retry: false,
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey: subscriptionKeys.all })
      queryClient.setQueriesData<ManageSubscriptionsApiInterface>(
        { queryKey: subscriptionKeys.all },
        data => {
          if (!data) return data
          const index = data.items.findIndex(item => item.id === subscription.id)
          if (index === -1 || data.items[index].status === 'cancelled') return data
          const items = [...data.items]
          items[index] = { ...items[index], status: 'cancelled' }
          return { ...data, items }
        },
      )
    },
  })

  const handleConfirm = async (): Promise<void> => {
    try {
      await cancel()
      onClose()
      toastManager.add({ type: 'success', title: t('profile.subscriptions.unsubscribed') })
    } catch (error_) {
      if (error_ instanceof BadRequest) {
        if (error_.errors.common) {
          setError(error_.errors.common.join(''))
        }
      } else if (error_ instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
      }
    }
  }

  return (
    <ConfirmationDialog.Root
      open={opened}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <ConfirmationDialog.Popup
        title={t('confirmation_modal.default_title')}
        description={t('profile.subscriptions.unsubscribe_confirmation')}
        closeProps={{ 'aria-label': t('global.close') }}
        actions={
          <>
            <Button variant="secondary" onClick={onClose} disabled={isFetching}>
              {t('confirmation_modal.cancel')}
            </Button>
            <Button
              variant="primary"
              aria-label={t('confirmation_modal.confirm')}
              isLoading={isFetching}
              onClick={handleConfirm}
            >
              {t('confirmation_modal.confirm')}
            </Button>
          </>
        }
        feedback={
          error && (
            <p role="alert" className="text-center text-base text-text-error">
              {error}
            </p>
          )
        }
      />
    </ConfirmationDialog.Root>
  )
}

export default CancelSubscriptionModal
